import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ChevronRight } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { useTranslate } from "../context/TranslateContext";
import { SONGS, getPerson } from "../mock/data";

export const Music = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const listenText = "Music is a first-class citizen here — film soundtracks, independent artists, classical Carnatic and Hindustani, folk, devotional, ghazal and the new sound of Indian pop and hip-hop.";

  return (
    <section id="music" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <Overline>Music</Overline>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
              {t("Every ")}<span className="italic text-gradient-gold">{t("sound")}</span>{t(" of India, one library")}
            </h2>
            <p className="mt-6 font-body text-lg leading-relaxed text-ash">
              {t("From playback legends to indie bedroom producers, trace every song to its singer, composer and lyricist. Film music and independent music, side by side.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Film", "Classical", "Carnatic", "Hindustani", "Folk", "Sufi", "Indie", "Hip-Hop", "Fusion"].map((g) => (
                <span key={g} className="rounded-full border border-edge px-4 py-1.5 font-body text-sm text-ash transition-colors duration-300 hover:border-gold hover:text-gold">
                  {t(g)}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              
              <Link to="/article/music-overview" data-testid="read-music-guide"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-body text-sm text-gold transition-colors hover:bg-gold/10">
                {t("Read: The Many Sounds of India")} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <Reveal>
            <div className="rounded-2xl border border-edge bg-surface/40 p-2 backdrop-blur">
              {SONGS.map((s, i) => (
                <motion.div key={s.title} whileHover={{ x: 6 }} transition={{ duration: 0.3 }}
                  data-testid={`song-row-${i}`} onClick={() => navigate(`/song/${s.id}`)}
                  className="group flex cursor-pointer items-center gap-4 rounded-xl px-4 py-4 transition-colors duration-300 hover:bg-black/5">
                  <button className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-edge text-gold transition-colors duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-ink">
                    <Play className="h-4 w-4 fill-current" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-body text-base text-cream">{s.title}</div>
                    <div className="truncate font-body text-sm text-ash">{getPerson(s.singer)?.name} · {t(s.genre)}</div>
                  </div>
                  <span className="font-body text-sm text-ash">{s.duration}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
